import { defineAsyncComponent } from 'vue';
import AddAddressModal from '~/components/modals/person/AddAddressModal/AddAddressModal.vue';
import AddAlternateNameModal from '~/components/modals/person/AddAlternateNameModal/AddAlternateNameModal.vue';
import AddChildConsent from '~/components/modals/person/AddContact/AddChildConsent.vue';
import AddChildForm from '~/components/modals/person/AddContact/AddChildForm.vue';
import AddContact from '~/components/modals/person/AddContact/AddContact.vue';
import AddMemberTypeSelect from '~/components/modals/person/AddContact/AddMemberTypeSelect.vue';
import AddEmailModal from '~/components/modals/person/AddEmailModal/AddEmailModal.vue';
import AddPhoneModal from '~/components/modals/person/AddPhoneModal/AddPhoneModal.vue';
import AssignLicenseModal from '~/components/modals/person/AssignLicenseModal/AssignLicenseModal.vue';
import GiveLicenseModal from '~/components/modals/person/GiveLicenseModal/GiveLicenseModal.vue';
import CallConnectedModal from '~/components/modals/person/CallConnectedModal/CallConnectedModal.vue';
import DeleteAccount from '~/components/modals/person/DeleteAccount/DeleteAccount.vue';
import EditDobModal from '~/components/modals/person/EditDobModal/EditDobModal.vue';
import EditNameModal from '~/components/modals/person/EditNameModal.vue';
import EmailVerification from '~/components/modals/person/EmailVerification/EmailVerification.vue';
import GiftCertModal from '~/components/modals/person/GiftCertModal/GiftCertModal.vue';
import GiftCertPurchased from '~/components/modals/person/GiftCertPurchasedModal/GiftCertPurchased.vue';
import ImageViewerModal from '~/components/modals/person/ImageViewerModal/ImageViewerModal.vue';
import OutgoingCallModal from '~/components/modals/person/OutgoingCallModal/OutgoingCallModal.vue';
import PhoneVerification from '~/components/modals/person/PhoneVerification/PhoneVerification.vue';

import ConnectAiModal from '~/components/modals/person/ConnectAiModal.vue';
import CreatePasskeyModal from '~/components/modals/person/CreatePasskeyModal/CreatePasskeyModal.vue';
import FeatureLockedModal from '~/components/modals/FeatureLockedModal.vue';
import ConfirmationModal from '~/components/modals/ConfirmationModal.vue';
import ExposureSources from '~/components/modals/person/ExposureSources/ExposureSources.vue';
import RemovalRequestModal from '~/components/modals/person/RemovalRequestModal/RemovalRequestModal.vue';
import RequestNewCompany from '~/components/modals/person/RequestNewCompany.vue';
import SubmitResearchRequest from '~/components/modals/person/SubmitResearchRequest.vue';
import StripePaymentModal from '~/components/modals/person/UpdatePaymentInfoModal/StripePaymentModal.vue';
import ProposalStripePaymentModal from '~/components/public/ProposalStripePaymentModal.vue';
import ExposureSideBar from '~/components/side-bar/person/ExposureSideBar.vue';
import type { Contact } from './person/contacts';
import type { Address, Aka, License } from './person/person';

//admin

// business
import PayInvoicesModal from '~/components/business/billing/PayInvoicesModal.vue';
import UpdateBusinessAddressModal from '~/components/business/settings/UpdateAddressModal.vue';
import UpdateBusinessDepartmentModal from '~/components/business/settings/UpdateDepartmentModal.vue';
import UpdateBusinessDomainModal from '~/components/business/settings/UpdateDomainModal.vue';
import UpdateBusinessPhoneModal from '~/components/business/settings/UpdatePhoneModal.vue';
import VerifyBusinessDomainModal from '~/components/business/settings/VerifyDomainModal.vue';

// business employee modals
import AddEmployeeAkaModal from '~/components/business/employees/AddEmployeeAkaModal.vue';
import AddEmployeeChildModal from '~/components/business/employees/AddEmployeeChildModal.vue';
import AddEmployeeEmailModal from '~/components/business/employees/AddEmployeeEmailModal.vue';
import AddEmployeeFamilyMemberModal from '~/components/business/employees/AddEmployeeFamilyMemberModal.vue';
import CreateEmployeeModal from '~/components/business/employees/CreateEmployeeModal.vue';
import EditEmployeeAddressModal from '~/components/business/employees/EditEmployeeAddressModal.vue';
import EditEmployeeDobModal from '~/components/business/employees/EditEmployeeDobModal.vue';
import EditEmployeeNameModal from '~/components/business/employees/EditEmployeeNameModal.vue';
import EditEmployeePhoneModal from '~/components/business/employees/EditEmployeePhoneModal.vue';
import EmployeeDetailsDrawer from '~/components/business/employees/EmployeeDetailsDrawer.vue';
import LicenseBlockDrawer from '~/components/business/billing/LicenseBlockDrawer.vue';
import ConsolidateBillingDrawer from '~/components/business/billing/ConsolidateBillingDrawer.vue';
import RemoveEmployeeModal from '~/components/business/employees/RemoveEmployeeModal.vue';
import UploadEmployeesModal from '~/components/business/employees/UploadEmployeesModal.vue';

//dev
import ManagePaymentMethodsModal from '~/components/modals/person/ManagePaymentMethodsModal/ManagePaymentMethodsModal.vue';
import type { DialogProps } from 'primevue/dialog';
import type { DynamicDialogInstance, DynamicDialogOptions } from 'primevue/dynamicdialogoptions';
import DeletionRequestModal from '~/components/modals/person/DeletionRequestModal/DeletionRequestModal.vue';
import GiftDependentsModal from '~/components/modals/person/LicenseModals/GiftDependentsModal.vue';
import RefundLicenseModal from '~/components/modals/person/LicenseModals/RefundLicenseModal.vue';
import type { GiftDependent } from 'pb-billing-ui/gift-dependents.ts';
import type { AssignLicenseModalData, PurchaseLicenseFor } from '~/types/licenses/licenses';
import type { Ref } from 'vue';
import type { Department, License as BusinessLicense, PayableInvoice } from '~/types/business';
import type { RoleListItem } from '~/types/roles';

export interface PbDialogProps extends DialogProps {
    // Receives whatever the modal passed to close(), so a caller can tell a completed action
    // from a dismissal. Handlers that ignore the argument stay valid.
    onClose?: (options?: { data?: any; type?: string }) => void;
    // Accessible name of the dialog. Defaults to the humanised ModalNames key; PrimeVue's own
    // `header` prop is not used because it renders a visible header bar.
    label?: string;
}

export enum ModalNames {
    // Personal modals
    FOOTPRINT_SESSION,
    GIFTCERT,
    GIFTCERT_PURCHASED,
    MANAGE_PAYMENT_METHODS,
    EMAIL_VERIFICATION,
    PHONE_VERIFICATION,
    ADD_CONTACT,
    ADD_MEMBER_TYPE_SELECT,
    ADD_CHILD_CONSENT,
    ADD_CHILD_FORM,
    EXPOSURE_SIDEBAR,
    REQUEST_NEW_COMPANY,
    DELETION_REQUEST,
    OUTGOING_CALL,
    OUTGOING_CALL_CONNECTED,
    ADD_EMAIL,
    ADD_PHONE,
    ADD_ALTERNATE_NAME,
    EDIT_NAME,
    ADD_ADDRESS,
    EDIT_DOB,
    STRIPE_PAYMENT,
    IMAGE_VIEWER,
    ASSIGN_LICENSE,
    GIVE_LICENSE,
    SUBMIT_RESEARCH_REQUEST,
    REFUND_LICENSE,
    GIFT_DEPENDENTS,
    DELETE_ACCOUNT,
    EXPOSURE_SOURCES,
    REMOVAL_REQUEST,
    CREATE_PASSKEY,
    CONNECT_AI,
    FEATURE_LOCKED,
    CONFIRMATION,

    // License modals

    // Business modals
    PAY_INVOICES,
    UPDATE_BUSINESS_PHONE,
    UPDATE_BUSINESS_ADDRESS,
    UPDATE_BUSINESS_DOMAIN,
    VERIFY_BUSINESS_DOMAIN,
    UPDATE_BUSINESS_DEPARTMENT,

    // Business employee modals
    CREATE_EMPLOYEE,
    UPLOAD_EMPLOYEES,
    EMPLOYEE_DETAILS_DRAWER,
    EDIT_EMPLOYEE_NAME,
    EDIT_EMPLOYEE_DOB,
    ADD_EMPLOYEE_EMAIL,
    ADD_EMPLOYEE_AKA,
    EDIT_EMPLOYEE_ADDRESS,
    EDIT_EMPLOYEE_PHONE,
    ADD_EMPLOYEE_CHILD,
    ADD_EMPLOYEE_FAMILY_MEMBER,
    REMOVE_EMPLOYEE,

    // Business billing modals
    LICENSE_BLOCK_DRAWER,
    CONSOLIDATE_BILLING_DRAWER,

    // Public (unauthenticated) modals
    PROPOSAL_STRIPE_PAYMENT,

    // Vault edit modals
    EDIT_ADDRESS,
    EDIT_ALTERNATE_NAME,
}

/**
 * A data field the modal reads LIVE, rather than as the snapshot the dialog opened on.
 *
 * `useDynamicDialog` hands the modal one object and never re-opens it, so a plain field states
 * what was true at the press. A modal whose contents narrow while it is open — the opener's list
 * shrinking under the member's own presses — needs the opener's source of truth instead, so the
 * field opts in by declaring this type and the opener passes a `Ref`/`ComputedRef`.
 *
 * Both sides of the union are real at runtime: PrimeVue stores the dialog instance in reactive
 * state, which unwraps a nested ref on read, while a test that provides `dialogRef` by hand does
 * not. So a modal reads such a field through Vue's `unref` — live and correctly typed either way —
 * and never through `.value`.
 *
 * Reactive by declaration only: a field not declared this way is a snapshot, and callers of a
 * snapshot field may not smuggle a ref into it.
 */
export type ReactiveModalData<T> = T | Readonly<Ref<T>>;

/**
 * How a dialog tells its opener a write is out: called with `true` as the write starts, and with
 * `false` as it ends, whichever way it ends, and as the dialog unmounts.
 *
 * The dialog refuses to close itself mid-write, but an opener that closes its dialogs as it
 * unmounts closes them past that refusal, and the reader never sees whether the write landed. So
 * an opener that passes this holds its route while a write is out.
 */
export type WritingListener = (writing: boolean) => void;

/** One entry in a confirmation's list: named, not described — see ConfirmationContent. */
export interface ConfirmationFeature {
    key: string;
    label: string;
    /**
     * Optional, and usually omitted. A consent dialog listing a full paragraph per entry buries
     * the sentence the member is actually consenting to; the entries read as a list of what is
     * being turned on, and the detail stays on the card they came from.
     */
    description?: string;
}

/** Everything the confirmation dialog draws. Every field may change while the dialog is open. */
export interface ConfirmationContent {
    // Supplied per invocation: an enabling label must never be inherited by a withdrawal or by a
    // multi-item submission.
    confirmLabel: string;
    features?: readonly ConfirmationFeature[];
    title?: string;
    description?: string;
    warning?: string;
    cancelLabel?: string;
    // Offered only where the caller can act on a narrowed set; a dialog confirming one indivisible
    // change leaves its list as a statement of what will happen.
    removable?: boolean;
}

// Type mapping for modal data requirements
export interface ModalDataMap {
    /**
     * Confirms an act Privacy Bee cannot reverse.
     *
     * `content` is reactive so the dialog can narrow under the member's own presses: `onRemove`
     * drops an entry from the OPENER's set, and the count, the title, the body and the confirm
     * label all follow from there. `close({ confirmed: true })` is the only confirmation; every
     * other way out is a cancellation.
     */
    [ModalNames.CONFIRMATION]: {
        content: ReactiveModalData<ConfirmationContent>;
        removeLabel?: (label: string) => string;
        onRemove?: (featureKey: string) => void;
    };
    [ModalNames.REFUND_LICENSE]: License;
    /**
     * Asks before the member stops the subscription other people's protection rides on, naming
     * them. `close({ confirmed: true })` is the member going ahead; every other way out keeps it.
     */
    [ModalNames.GIFT_DEPENDENTS]: { dependents: readonly GiftDependent[] };
    [ModalNames.EMAIL_VERIFICATION]: { email: string; fromExposure?: boolean; exposureData?: any };
    [ModalNames.PHONE_VERIFICATION]: {
        phone_number: string;
        country_code: string;
        method: string;
        fromExposure?: boolean;
        exposureData?: any;
    };
    [ModalNames.ADD_PHONE]: {
        phone_number?: string;
        country_code?: string;
        method?: string;
        fromExposure?: boolean;
        exposureData?: any;
    };
    [ModalNames.ADD_EMAIL]: { email?: string; fromExposure?: boolean; exposureData?: any };
    [ModalNames.ADD_ADDRESS]: { address?: any; fromExposure?: boolean; exposureData?: any };
    [ModalNames.ADD_CONTACT]: {
        contact?: any;
        /** What a caller already knows about the member, filled into the form's own fields. */
        prefill?: { first_name?: string; last_name?: string; email?: string; phone?: { country_code: string; phone_number: string } };
        /** An existing member to complete. Present means edit mode; absent means add mode. */
        member?: Contact;
        /**
         * Why the form opened on `member`. `assign` continues to the license picker on save;
         * `direct` and `purchase` save and hand control back, because a purchase never starts
         * as a side effect of saving an email address.
         */
        reason?: 'direct' | 'assign' | 'purchase';
        fromExposure?: boolean;
        exposureData?: any;
    };
    [ModalNames.ADD_ALTERNATE_NAME]: { fullName?: string; fromExposure?: boolean; exposureData?: any };
    [ModalNames.EDIT_ADDRESS]: { editAddress: Address };
    [ModalNames.EDIT_ALTERNATE_NAME]: { editAka: Aka };
    [ModalNames.IMAGE_VIEWER]: { imageUrl: string; altText?: string };
    [ModalNames.ASSIGN_LICENSE]: AssignLicenseModalData;
    /** `feature`: the locked feature the giver came from; the dialog then lists only the spares that carry it. */
    [ModalNames.GIVE_LICENSE]: { contact: PurchaseLicenseFor; feature?: string };
    [ModalNames.DELETION_REQUEST]: { company: { id: number; name: string; type: string; logo: string; url: string; slug: string } };
    [ModalNames.REQUEST_NEW_COMPANY]: { name?: string };
    /** Human name of the gated capability, used verbatim in the dialog's title. */
    [ModalNames.FEATURE_LOCKED]: { feature: string };
    [ModalNames.EXPOSURE_SOURCES]: { exposures: any[] };
    /**
     * The invoices this press is paying — one row, or every open one for a Pay all.
     * `cardBrand` / `cardLastFour` are the billing summary's card, used for the
     * multi-invoice confirmation, which has no single pay preview to read the card off.
     * `hasPaymentMethod` is the summary's word on whether there is a card at all, which
     * the brand cannot settle: a card Stripe has not described has no brand.
     */
    [ModalNames.PAY_INVOICES]: {
        companyId: number;
        invoices: PayableInvoice[];
        hasPaymentMethod?: boolean;
        cardBrand?: string | null;
        cardLastFour?: string | null;
        /** Hears the whole pay run, every charge and bank step in it. */
        onWriting?: WritingListener;
    };
    [ModalNames.UPDATE_BUSINESS_PHONE]: { companyId: number; phone: any | null; isPrimary: boolean };
    [ModalNames.UPDATE_BUSINESS_ADDRESS]: { companyId: number; address: any | null };
    [ModalNames.UPDATE_BUSINESS_DOMAIN]: { companyId: number; domain: any | null; isPrimary: boolean };
    [ModalNames.VERIFY_BUSINESS_DOMAIN]: { companyId: number; domain: any };
    [ModalNames.UPDATE_BUSINESS_DEPARTMENT]: { companyId: number; department: any | null };

    // Business employee modals
    [ModalNames.CREATE_EMPLOYEE]: { companyId: number; companySlug?: string; onRefresh?: () => Promise<void> };
    [ModalNames.UPLOAD_EMPLOYEES]: { companyId: number; companySlug: string };
    /**
     * The roster page loads the company's licenses, departments and roles before
     * it opens the drawer and hands them over, so the drawer asks only for the
     * one employee.
     *
     * The three lists are READ-ONLY on the drawer's side. The page's license list
     * is not one endpoint's answer: it layers `getLicenseInformation` over
     * `getLicenses`, which corrects the availability counts and adds the
     * information-only SKUs the availability cards are drawn from. A drawer that
     * wrote a bare `getLicenses` response back into it would drop that second
     * layer and leave the cards behind it wrong. When a seat write moves the
     * counts, the drawer calls `onRefresh` and the page re-runs both steps — the
     * page is the only writer.
     */
    [ModalNames.EMPLOYEE_DETAILS_DRAWER]: {
        companyId: number;
        employeeId: number;
        companySlug: string;
        licenses: Readonly<Ref<BusinessLicense[]>>;
        departments: Readonly<Ref<Department[]>>;
        roles: Readonly<Ref<RoleListItem[]>>;
        /** Reloads the roster AND the merged license list; resolves when both have landed. */
        onRefresh: () => Promise<void>;
        /** Re-reads only the seat counts into the merged list (dropdown open, back from checkout). */
        onRefreshSeats?: () => Promise<unknown>;
    };
    [ModalNames.EDIT_EMPLOYEE_NAME]: { companyId: number; employeeId: number; employee: any };
    [ModalNames.EDIT_EMPLOYEE_DOB]: { companyId: number; employeeId: number; employee: any };
    [ModalNames.ADD_EMPLOYEE_EMAIL]: { companyId: number; employeeId: number };
    // `aka` present → modal opens in edit mode; absent → add mode.
    [ModalNames.ADD_EMPLOYEE_AKA]: { companyId: number; employeeId: number; aka?: { id: number; first_name: string; middle_name?: string | null; last_name: string } };
    [ModalNames.EDIT_EMPLOYEE_ADDRESS]: { companyId: number; employeeId: number; address: any | null };
    [ModalNames.EDIT_EMPLOYEE_PHONE]: { companyId: number; employeeId: number; phone: any | null };
    [ModalNames.ADD_EMPLOYEE_CHILD]: { companyId: number; employeeId: number; employeeName: string; consentSigned?: boolean };
    [ModalNames.ADD_EMPLOYEE_FAMILY_MEMBER]: { companyId: number; employeeId: number; employeeName: string };
    [ModalNames.REMOVE_EMPLOYEE]: { companyId: number; employeeId: number; employeeName: string };

    // Business billing modals
    [ModalNames.LICENSE_BLOCK_DRAWER]: {
        companyId: number;
        licenseId: number;
        licenseName: string;
        blockId: string;
        nextBillDate: string | null;
        renewalPriceCents: number;
        /**
         * How many licenses in the block auto-renew. Live, not a snapshot: the drawer's own
         * toggles change it, and the header states whether the block renews at all.
         */
        autoRenewOn: ReactiveModalData<number>;
        onChange?: () => void;
    };
    [ModalNames.CONSOLIDATE_BILLING_DRAWER]: {
        companyId: number;
        hasPaymentMethod?: boolean;
        onScheduled?: () => void;
        onWriting?: WritingListener;
    };

    // Public modals
    [ModalNames.PROPOSAL_STRIPE_PAYMENT]: {
        onSubmitted: (payload: { paymentMethodId: string; firstName: string; lastName: string }) => Promise<boolean | void> | boolean | void;
    };
    // `companyId` switches the modal to the COMPANY's shared card collection; without
    // it, it manages the signed-in person's own cards. `onWriting` hears every change to the
    // cards — an add, a removal, a new primary, an edit — not only a card being added.
    [ModalNames.MANAGE_PAYMENT_METHODS]: { companyId?: number; companyName?: string; onWriting?: WritingListener };
}

// Helper type to get data type for a specific modal
export type ModalData<T extends ModalNames> = T extends keyof ModalDataMap ? ModalDataMap[T] : never;

// Modals that carry a data payload
export type ModalWithData = keyof ModalDataMap;

// Modals whose payload has at least one required field — data must be passed when opening
export type ModalWithRequiredData = {
    [K in ModalWithData]: Partial<ModalDataMap[K]> extends ModalDataMap[K] ? never : K;
}[ModalWithData];

// Modals whose payload is entirely optional — data may be omitted when opening
export type ModalWithOptionalData = Exclude<ModalWithData, ModalWithRequiredData>;

// Modals that take no data payload
export type ModalWithoutData = Exclude<ModalNames, ModalWithData>;

// Modal component mapping for better autocomplete and navigation
// Load the scanner only when its dialog opens; eagerly importing its store from
// the global registry creates a cycle through API-backed employee dialogs.
const FootprintSessionModal = defineAsyncComponent(() => import('~/components/modals/person/FootprintSessionModal.vue'));

export const ModalComponents = {
    [ModalNames.FOOTPRINT_SESSION]: () => FootprintSessionModal,
    [ModalNames.PHONE_VERIFICATION]: () => PhoneVerification,
    [ModalNames.EMAIL_VERIFICATION]: () => EmailVerification,
    [ModalNames.ADD_PHONE]: () => AddPhoneModal,
    [ModalNames.ADD_EMAIL]: () => AddEmailModal,
    [ModalNames.ADD_ADDRESS]: () => AddAddressModal,
    [ModalNames.ADD_ALTERNATE_NAME]: () => AddAlternateNameModal,
    [ModalNames.EDIT_ADDRESS]: () => AddAddressModal,
    [ModalNames.EDIT_ALTERNATE_NAME]: () => AddAlternateNameModal,
    [ModalNames.EDIT_NAME]: () => EditNameModal,
    [ModalNames.EDIT_DOB]: () => EditDobModal,
    [ModalNames.ADD_CONTACT]: () => AddContact,
    [ModalNames.ADD_MEMBER_TYPE_SELECT]: () => AddMemberTypeSelect,
    [ModalNames.ADD_CHILD_CONSENT]: () => AddChildConsent,
    [ModalNames.ADD_CHILD_FORM]: () => AddChildForm,
    [ModalNames.MANAGE_PAYMENT_METHODS]: () => ManagePaymentMethodsModal,
    [ModalNames.STRIPE_PAYMENT]: () => StripePaymentModal,
    [ModalNames.GIFTCERT]: () => GiftCertModal,
    [ModalNames.GIFTCERT_PURCHASED]: () => GiftCertPurchased,
    [ModalNames.REQUEST_NEW_COMPANY]: () => RequestNewCompany,
    [ModalNames.FEATURE_LOCKED]: () => FeatureLockedModal,
    [ModalNames.CONFIRMATION]: () => ConfirmationModal,
    [ModalNames.DELETION_REQUEST]: () => DeletionRequestModal,
    [ModalNames.OUTGOING_CALL]: () => OutgoingCallModal,
    [ModalNames.OUTGOING_CALL_CONNECTED]: () => CallConnectedModal,
    [ModalNames.IMAGE_VIEWER]: () => ImageViewerModal,
    [ModalNames.ASSIGN_LICENSE]: () => AssignLicenseModal,
    [ModalNames.GIVE_LICENSE]: () => GiveLicenseModal,
    [ModalNames.SUBMIT_RESEARCH_REQUEST]: () => SubmitResearchRequest,
    [ModalNames.EXPOSURE_SIDEBAR]: () => ExposureSideBar,
    [ModalNames.REFUND_LICENSE]: () => RefundLicenseModal,
    [ModalNames.GIFT_DEPENDENTS]: () => GiftDependentsModal,
    [ModalNames.DELETE_ACCOUNT]: () => DeleteAccount,
    [ModalNames.EXPOSURE_SOURCES]: () => ExposureSources,
    [ModalNames.REMOVAL_REQUEST]: () => RemovalRequestModal,
    [ModalNames.PAY_INVOICES]: () => PayInvoicesModal,
    [ModalNames.UPDATE_BUSINESS_PHONE]: () => UpdateBusinessPhoneModal,
    [ModalNames.UPDATE_BUSINESS_ADDRESS]: () => UpdateBusinessAddressModal,
    [ModalNames.UPDATE_BUSINESS_DOMAIN]: () => UpdateBusinessDomainModal,
    [ModalNames.VERIFY_BUSINESS_DOMAIN]: () => VerifyBusinessDomainModal,
    [ModalNames.UPDATE_BUSINESS_DEPARTMENT]: () => UpdateBusinessDepartmentModal,
    [ModalNames.CREATE_EMPLOYEE]: () => CreateEmployeeModal,
    [ModalNames.UPLOAD_EMPLOYEES]: () => UploadEmployeesModal,
    [ModalNames.EMPLOYEE_DETAILS_DRAWER]: () => EmployeeDetailsDrawer,
    [ModalNames.EDIT_EMPLOYEE_NAME]: () => EditEmployeeNameModal,
    [ModalNames.EDIT_EMPLOYEE_DOB]: () => EditEmployeeDobModal,
    [ModalNames.ADD_EMPLOYEE_EMAIL]: () => AddEmployeeEmailModal,
    [ModalNames.ADD_EMPLOYEE_AKA]: () => AddEmployeeAkaModal,
    [ModalNames.EDIT_EMPLOYEE_ADDRESS]: () => EditEmployeeAddressModal,
    [ModalNames.EDIT_EMPLOYEE_PHONE]: () => EditEmployeePhoneModal,
    [ModalNames.ADD_EMPLOYEE_CHILD]: () => AddEmployeeChildModal,
    [ModalNames.ADD_EMPLOYEE_FAMILY_MEMBER]: () => AddEmployeeFamilyMemberModal,
    [ModalNames.REMOVE_EMPLOYEE]: () => RemoveEmployeeModal,
    [ModalNames.LICENSE_BLOCK_DRAWER]: () => LicenseBlockDrawer,
    [ModalNames.CONSOLIDATE_BILLING_DRAWER]: () => ConsolidateBillingDrawer,
    [ModalNames.CREATE_PASSKEY]: () => CreatePasskeyModal,
    [ModalNames.CONNECT_AI]: () => ConnectAiModal,
    [ModalNames.PROPOSAL_STRIPE_PAYMENT]: () => ProposalStripePaymentModal,
} as const;

export type TypedDialogOptions<T> = Omit<DynamicDialogOptions, 'data'> & { data: T };
export type TypedDialogInstance<T> = Omit<DynamicDialogInstance, 'data' | 'options'> & {
    data: T;
    options: TypedDialogOptions<T>;
};

// Helper type to get data type for a specific modal component
export type ModalDataForComponent<T extends keyof typeof ModalComponents> =
  T extends keyof ModalDataMap ? ModalDataMap[T] : never;
